/**
 * Manage Holding Modal
 * Modal for creating and editing manual holdings
 * WP-MANUAL-HOLD Phase 3
 */
import { useState, useEffect } from 'react';
import {
  useCreateHolding,
  useUpdateHolding,
  useHolding,
} from '@/hooks/useAllocation';
import Button from '@/components/ui/Button';
import {
  ASSET_CLASS_DISPLAY_NAMES,
  type AssetClass,
  type CreateHoldingRequest,
  type UpdateHoldingRequest,
} from '@/types/allocation';

interface ManageHoldingModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Account ID for create mode */
  accountId?: string;
  /** Holding ID for edit mode (mutually exclusive with accountId) */
  holdingId?: string;
}

export default function ManageHoldingModal({
  isOpen,
  onClose,
  accountId,
  holdingId,
}: ManageHoldingModalProps) {
  const isEditMode = !!holdingId;

  // Fetch holding data for edit mode
  const { data: holdingData, isLoading: isLoadingHolding } = useHolding(holdingId ?? null);

  // Mutations
  const { mutate: createHolding, isPending: isCreating } = useCreateHolding();
  const { mutate: updateHolding, isPending: isUpdating } = useUpdateHolding();

  // Form state
  const [name, setName] = useState('');
  const [tickerSymbol, setTickerSymbol] = useState('');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [priceAsOf, setPriceAsOf] = useState('');
  const [costBasis, setCostBasis] = useState('');
  const [assetClass, setAssetClass] = useState<AssetClass | ''>('');
  const [currency, setCurrency] = useState('USD');

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form for edit mode
  useEffect(() => {
    if (isEditMode && holdingData) {
      setName(holdingData.name);
      setTickerSymbol(holdingData.tickerSymbol ?? '');
      setQuantity(holdingData.quantity);
      setPrice(holdingData.price ?? '');
      setPriceAsOf(holdingData.priceAsOf ?? '');
      setCostBasis(holdingData.costBasis ?? '');
      setAssetClass(holdingData.assetClass);
      setCurrency(holdingData.currency);
    }
  }, [isEditMode, holdingData]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setTickerSymbol('');
      setQuantity('');
      setPrice('');
      setPriceAsOf('');
      setCostBasis('');
      setAssetClass('');
      setCurrency('USD');
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!quantity.trim()) {
      newErrors.quantity = 'Quantity is required';
    } else {
      const qty = parseFloat(quantity);
      if (isNaN(qty) || qty === 0) {
        newErrors.quantity = 'Quantity cannot be zero';
      }
    }

    if (price && isNaN(parseFloat(price))) {
      newErrors.price = 'Price must be a valid number';
    }

    if (costBasis && isNaN(parseFloat(costBasis))) {
      newErrors.costBasis = 'Cost basis must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    if (isEditMode && holdingId) {
      // Update existing holding
      const updateData: UpdateHoldingRequest = {
        name: name.trim(),
        tickerSymbol: tickerSymbol.trim() || null,
        quantity,
        price: price || null,
        priceAsOf: priceAsOf || null,
        costBasis: costBasis || null,
        currency,
      };

      updateHolding(
        { holdingId, data: updateData },
        {
          onSuccess: () => {
            handleClose();
          },
        }
      );
    } else if (accountId) {
      // Create new holding
      const createData: CreateHoldingRequest = {
        accountId,
        name: name.trim(),
        tickerSymbol: tickerSymbol.trim() || null,
        quantity,
        price: price || null,
        priceAsOf: priceAsOf || null,
        costBasis: costBasis || null,
        currency,
        assetClass: assetClass || null,
      };

      createHolding(createData, {
        onSuccess: () => {
          handleClose();
        },
      });
    }
  };

  const handleClose = () => {
    onClose();
  };

  const isPending = isCreating || isUpdating;
  const isLoadingForm = isEditMode && isLoadingHolding;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg transform bg-white rounded-lg shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditMode ? 'Edit Holding' : 'Add Manual Holding'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {isLoadingForm ? (
              <div className="animate-pulse space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-10 bg-gray-200 rounded" />
                ))}
              </div>
            ) : (
              <>
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Apple Inc."
                    className={`w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                  )}
                </div>

                {/* Ticker Symbol */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ticker Symbol
                  </label>
                  <input
                    type="text"
                    value={tickerSymbol}
                    onChange={(e) => setTickerSymbol(e.target.value.toUpperCase())}
                    placeholder="e.g., AAPL"
                    maxLength={20}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="e.g., 100"
                    className={`w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      errors.quantity ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.quantity && (
                    <p className="mt-1 text-xs text-red-600">{errors.quantity}</p>
                  )}
                </div>

                {/* Price per Unit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price per Unit
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="e.g., 150.50"
                    className={`w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      errors.price ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.price && (
                    <p className="mt-1 text-xs text-red-600">{errors.price}</p>
                  )}
                </div>

                {/* Price As Of Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price As Of Date
                  </label>
                  <input
                    type="date"
                    value={priceAsOf}
                    onChange={(e) => setPriceAsOf(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Cost Basis */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Cost Basis
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={costBasis}
                    onChange={(e) => setCostBasis(e.target.value)}
                    placeholder="e.g., 10000.00"
                    className={`w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      errors.costBasis ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.costBasis && (
                    <p className="mt-1 text-xs text-red-600">{errors.costBasis}</p>
                  )}
                </div>

                {/* Asset Class (create mode only) */}
                {!isEditMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asset Class
                    </label>
                    <select
                      value={assetClass}
                      onChange={(e) => setAssetClass(e.target.value as AssetClass | '')}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Auto-detect</option>
                      {Object.entries(ASSET_CLASS_DISPLAY_NAMES).map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Leave as auto-detect to classify based on ticker symbol
                    </p>
                  </div>
                )}

                {/* Currency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                    <option value="CHF">CHF - Swiss Franc</option>
                  </select>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <Button variant="ghost" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isLoadingForm}
              isLoading={isPending}
            >
              {isEditMode ? 'Save Changes' : 'Add Holding'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}
